import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { IoClose, IoCloudUploadOutline, IoDocumentOutline } from "react-icons/io5";

const IDLE = "idle";
const UPLOADING = "uploading";
const DONE = "done";
const ERROR = "error";

const DropzoneUpload = ({ label, badge, badgeVariant, status, filename, onSelect, onRetry }) => {
    const inputRef = useRef(null);

    return (
        <div className={`check-register-dropzone check-register-dropzone--${status} check-register-dropzone--type-${badgeVariant}`}>
            <input
                ref={inputRef}
                type="file"
                accept=".xlsx"
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onSelect(file);
                    e.target.value = "";
                }}
            />
            <div className="check-register-dropzone__header">
                <span className={`check-register-dropzone__badge check-register-dropzone__badge--${badgeVariant}`}>
                    {badge}
                </span>
            </div>
            {status === IDLE && (
                <button
                    type="button"
                    className="check-register-dropzone__area"
                    onClick={() => inputRef.current?.click()}
                >
                    <IoCloudUploadOutline className="check-register-dropzone__icon" size={22} aria-hidden />
                    <span className="check-register-dropzone__area-label">{label}</span>
                    <span className="check-register-dropzone__hint">.xlsx · click to browse</span>
                </button>
            )}
            {status === UPLOADING && (
                <div className="check-register-dropzone__area check-register-dropzone__area--uploading" aria-busy="true">
                    <span className="check-register-dropzone__uploading">Uploading…</span>
                </div>
            )}
            {status === DONE && (
                <div className="check-register-dropzone__area check-register-dropzone__area--done">
                    <IoDocumentOutline className="check-register-dropzone__done-icon" size={18} aria-hidden />
                    <span className="check-register-dropzone__done-name">{filename}</span>
                </div>
            )}
            {status === ERROR && (
                <div className="check-register-dropzone__area check-register-dropzone__area--error" role="alert">
                    <span className="check-register-dropzone__error-text">Upload failed</span>
                    <button type="button" className="check-register-dropzone__retry" onClick={onRetry}>
                        Retry
                    </button>
                </div>
            )}
        </div>
    );
};

const CheckRegisterActionDialog = ({ doc, action, onClose, onSuccess, mutations }) => {
    const [uploadUrls, setUploadUrls] = useState(null);
    const [phiStatus, setPhiStatus] = useState(IDLE);
    const [phiFilename, setPhiFilename] = useState(null);
    const [noPhiStatus, setNoPhiStatus] = useState(IDLE);
    const [noPhiFilename, setNoPhiFilename] = useState(null);
    const fetchedRef = useRef(false);

    const fetchUploadUrls = useCallback(async () => {
        try {
            const result = await mutations.createUploadUrl.mutateAsync({ accountHccId: doc.accountHccId });
            setUploadUrls(result);
        } catch {
            // error handled by hook
        }
    }, [doc.accountHccId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (action === "reject" && !fetchedRef.current) {
            fetchedRef.current = true;
            fetchUploadUrls();
        }
    }, [action, fetchUploadUrls]);

    const uploadFile = async (file, uploadUrl, contentType, setStatus, setFilename) => {
        setStatus(UPLOADING);
        setFilename(file.name);
        try {
            const res = await fetch(uploadUrl, {
                method: "PUT",
                body: file,
                headers: { "Content-Type": contentType },
            });
            if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
            setStatus(DONE);
        } catch {
            setStatus(ERROR);
        }
    };

    const handlePhiSelect = (file) => {
        if (!uploadUrls) return;
        uploadFile(file, uploadUrls.phi.uploadUrl, uploadUrls.contentType, setPhiStatus, setPhiFilename);
    };

    const handleNoPhiSelect = (file) => {
        if (!uploadUrls) return;
        uploadFile(file, uploadUrls.noPhi.uploadUrl, uploadUrls.contentType, setNoPhiStatus, setNoPhiFilename);
    };

    const handleRetry = async (which) => {
        fetchedRef.current = false;
        await fetchUploadUrls();
        fetchedRef.current = true;
        if (which === "phi") setPhiStatus(IDLE);
        else setNoPhiStatus(IDLE);
    };

    const handleAcceptConfirm = async () => {
        await mutations.acceptDoc.mutateAsync({ sk: doc.sk, accountHccId: doc.accountHccId });
        onSuccess();
        onClose();
    };

    const handleRejectConfirm = async () => {
        if (phiStatus !== DONE || noPhiStatus !== DONE) return;
        await mutations.rejectWithFiles.mutateAsync({
            sk: doc.sk,
            accountHccId: doc.accountHccId,
            phiStagingKey: uploadUrls.phi.stagingKey,
            noPhiStagingKey: uploadUrls.noPhi.stagingKey,
        });
        onSuccess();
        onClose();
    };

    const isSubmitting = mutations.rejectWithFiles.isPending || mutations.acceptDoc.isPending;

    const handleEscapeClose = () => {
        if (isSubmitting) return;
        if (action === "reject" && (phiStatus === UPLOADING || noPhiStatus === UPLOADING)) return;
        onClose();
    };

    const uploadedCount = [phiStatus, noPhiStatus].filter((s) => s === DONE).length;
    const bothUploaded = uploadedCount === 2;

    return (
        <Dialog.Root open onOpenChange={(open) => { if (!open) handleEscapeClose(); }}>
            <Dialog.Portal>
                <Dialog.Overlay className="invoice-action-dialog__overlay" />
                <Dialog.Content
                    className="invoice-action-dialog__content"
                    aria-describedby={undefined}
                    aria-busy={isSubmitting}
                >
                    {isSubmitting && (
                        <div className="invoice-action-dialog__submit-overlay" aria-hidden>
                            <span className="invoice-action-dialog__submit-spinner" />
                            <span className="invoice-action-dialog__submit-label">Submitting…</span>
                        </div>
                    )}
                    <div className="invoice-action-dialog__header">
                        <Dialog.Title className="invoice-action-dialog__title">
                            {action === "accept" ? "Accept" : "Reject"} Check Register
                        </Dialog.Title>
                        <button
                            type="button"
                            className="invoice-action-dialog__close"
                            aria-label="Close dialog"
                            onClick={handleEscapeClose}
                            disabled={isSubmitting}
                        >
                            <IoClose aria-hidden size={20} />
                        </button>
                    </div>

                    <div className="invoice-action-dialog__body">
                        <dl className="invoice-action-dialog__summary">
                            <div className="invoice-action-dialog__summary-row">
                                <dt>Account Name</dt>
                                <dd>{doc.accountName}</dd>
                            </div>
                            <div className="invoice-action-dialog__summary-row">
                                <dt>Account ID</dt>
                                <dd>{doc.accountHccId}</dd>
                            </div>
                            <div className="invoice-action-dialog__summary-row">
                                <dt>Run Date</dt>
                                <dd>{doc.runDate || "—"}</dd>
                            </div>
                        </dl>

                        {action === "reject" && (
                            <div className="check-register-dialog__uploads">
                                <DropzoneUpload
                                    label="Upload PHI File"
                                    badge="PHI"
                                    badgeVariant="phi"
                                    status={phiStatus}
                                    filename={phiFilename}
                                    onSelect={handlePhiSelect}
                                    onRetry={() => handleRetry("phi")}
                                />
                                <DropzoneUpload
                                    label="Upload Non-PHI File"
                                    badge="Non-PHI"
                                    badgeVariant="nophi"
                                    status={noPhiStatus}
                                    filename={noPhiFilename}
                                    onSelect={handleNoPhiSelect}
                                    onRetry={() => handleRetry("noPhi")}
                                />
                            </div>
                        )}
                    </div>

                    <div className="invoice-action-dialog__footer invoice-action-dialog__footer--stacked">
                        {action === "reject" && (
                            <p
                                className={`check-register-dialog__file-progress${bothUploaded ? " check-register-dialog__file-progress--complete" : ""}`}
                                role="status"
                            >
                                {uploadedCount} of 2 files uploaded
                            </p>
                        )}
                        <div className="check-register-dialog__footer-actions">
                            <button
                                type="button"
                                className="invoice-action-dialog__btn invoice-action-dialog__btn--cancel"
                                onClick={handleEscapeClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            {action === "accept" ? (
                                <button
                                    type="button"
                                    className="invoice-action-dialog__btn invoice-action-dialog__btn--confirm-accept"
                                    onClick={handleAcceptConfirm}
                                    disabled={mutations.acceptDoc.isPending}
                                >
                                    {mutations.acceptDoc.isPending ? (
                                        <><span className="invoice-action-dialog__spinner" aria-hidden /> Submitting…</>
                                    ) : "Confirm Accept"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="invoice-action-dialog__btn invoice-action-dialog__btn--confirm-reject"
                                    disabled={!bothUploaded || mutations.rejectWithFiles.isPending}
                                    title={!bothUploaded ? "Upload both files to confirm" : undefined}
                                    onClick={handleRejectConfirm}
                                >
                                    {mutations.rejectWithFiles.isPending ? (
                                        <><span className="invoice-action-dialog__spinner" aria-hidden /> Submitting…</>
                                    ) : "Accept Manual Revised File"}
                                </button>
                            )}
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default CheckRegisterActionDialog;
